import { useEffect, useRef, useState } from "react";
import {
  wordpressPostsValueSchema,
  type FieldDefinition,
  type WordPressPostsValue,
} from "@qassist/schemas";
import { api } from "./api.js";

export function WordPressPostsField({
  field,
  value,
  onChange,
}: {
  field: FieldDefinition;
  value: unknown;
  onChange(value: unknown): void;
}) {
  const current = value && typeof value === "object" ? (value as Partial<WordPressPostsValue>) : {};
  const apiUrl = current.apiUrl ?? "";
  const postType = current.postType ?? "posts";
  const limit = current.limit ?? 10;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [loadedCount, setLoadedCount] = useState<number | null>(null);
  const [retry, setRetry] = useState(0);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    let cancelled = false;
    setError("");
    setLoadedCount(null);
    setLoading(false);
    const source = { mode: "wordpress" as const, apiUrl, postType, limit };
    if (!wordpressPostsValueSchema.safeParse(source).success) return;
    const timer = window.setTimeout(() => {
      setLoading(true);
      void api<{ posts: NonNullable<WordPressPostsValue["posts"]> }>(
        "/api/v1/client/wordpress-posts/preview",
        { method: "POST", body: JSON.stringify(source) },
      )
        .then((result) => {
          if (!cancelled) {
            onChangeRef.current({ ...source, posts: result.posts });
            setLoadedCount(result.posts.length);
          }
        })
        .catch((reason: unknown) => {
          if (!cancelled)
            setError(
              reason instanceof Error
                ? reason.message
                : "Could not connect to WordPress. Check the URL and post type, then retry.",
            );
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 700);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [apiUrl, postType, limit, retry]);

  const update = (changes: Partial<WordPressPostsValue>) =>
    onChange({ mode: "wordpress", apiUrl, postType, limit, ...changes });
  return (
    <fieldset className="dynamic-field-group">
      <legend>
        {field.label}
        {field.required ? " *" : ""}
      </legend>
      <label>
        <span>WordPress wp-json URL</span>
        <input
          type="url"
          required={field.required}
          maxLength={2000}
          value={apiUrl}
          placeholder="https://example.com/wp-json"
          onChange={(event) => update({ apiUrl: event.target.value })}
        />
      </label>
      <label>
        <span>REST post type</span>
        <input
          required={field.required}
          pattern="[a-z0-9][a-z0-9_-]{0,63}"
          maxLength={64}
          value={postType}
          placeholder="posts"
          onChange={(event) => update({ postType: event.target.value })}
        />
      </label>
      <small className="field-help">
        Use the REST endpoint slug: usually posts, or the custom post type’s rest_base. It must be
        public and expose title and excerpt.
      </small>
      <label>
        <span>Maximum posts</span>
        <input
          type="number"
          min={1}
          max={100}
          required
          value={limit}
          onChange={(event) => update({ limit: event.target.valueAsNumber })}
        />
      </label>
      <button
        type="button"
        className="button button--secondary"
        disabled={
          loading ||
          !wordpressPostsValueSchema.safeParse({ mode: "wordpress", apiUrl, postType, limit })
            .success
        }
        onClick={() => setRetry((count) => count + 1)}
      >
        {loading ? "Connecting…" : "Test WordPress connection"}
      </button>
      {loading ? <p role="status">Loading post titles and excerpts…</p> : null}
      {loadedCount !== null ? (
        <p role="status">
          {loadedCount
            ? `${loadedCount} post${loadedCount === 1 ? "" : "s"} loaded. The preview uses these titles and excerpts.`
            : "Connected. No published posts are available for this post type."}
        </p>
      ) : null}
      {error ? (
        <p className="form-error" role="alert">
          {error}
        </p>
      ) : null}
      <small className="field-help">
        Titles and excerpts refresh every minute while online. Previously loaded posts remain
        available for offline playback. Customize appearance and looping in the slide-type builder.
      </small>
    </fieldset>
  );
}
